/**
 * Phase 10D: Analytics & Telemetry (Spec Section 19 + 23).
 * Mixpanel-compatible event taxonomy. No-op if window.mixpanel not loaded.
 * Privacy: track counts/IDs/percentages only; do NOT track names, policy numbers, exact premiums.
 */

function getTracker() {
  if (typeof window === 'undefined') return null;
  return window.mixpanel && typeof window.mixpanel.track === 'function' ? window.mixpanel : null;
}

export function track(eventName, properties = {}) {
  const mixpanel = getTracker();
  if (mixpanel) {
    try {
      mixpanel.track(eventName, properties);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[analytics]', e);
    }
  }
}

// —— Category 1: Page Navigation ——

/** Dashboard viewed: filter, policiesDisplayed, betterDealsCount, source */
export function trackRenewalsDashboardViewed(props = {}) {
  track('Renewals Dashboard Viewed', {
    filter: props.filter ?? 'all',
    policiesDisplayed: props.policiesDisplayed ?? props.policiesCount ?? 0,
    betterDealsCount: props.betterDealsCount ?? 0,
    source: props.source ?? 'navbar',
  });
}

/** Details page viewed */
export function trackRenewalDetailsViewed(props = {}) {
  track('Renewal Details Viewed', {
    policyId: props.policyId,
    hasRecommendations: props.hasRecommendations ?? false,
    betterDealsCount: props.betterDealsCount ?? 0,
    daysUntilExpiry: props.daysUntilExpiry,
    defaultTab: props.defaultTab ?? 'overview',
  });
}

/** Tab changed on details page */
export function trackRenewalTabChanged(props = {}) {
  track('Renewal Tab Changed', {
    policyId: props.policyId,
    fromTab: props.fromTab,
    toTab: props.toTab,
  });
}

// —— Category 2: User Actions ——

/** Check initiated */
export function trackRenewalCheckStarted(props = {}) {
  track('Renewal Check Started', {
    policyId: props.policyId,
    checkType: props.checkType ?? 'manual',
    daysUntilExpiry: props.daysUntilExpiry,
    lastCheckAge: props.lastCheckAge,
  });
}

/** Check completed (alternativesFound, betterDealsFound, topSavings, duration ms) */
export function trackRenewalCheckCompleted(props = {}) {
  track('Renewal Check Completed', {
    policyId: props.policyId,
    alternativesFound: props.alternativesFound ?? 0,
    betterDealsFound: props.betterDealsFound ?? 0,
    topSavings: props.topSavings,
    duration: props.duration,
  });
}

/** Recommendation viewed (position = 1 for best) */
export function trackRecommendationViewed(props = {}) {
  track('Recommendation Viewed', {
    recommendationId: props.recommendationId,
    position: props.position ?? 1,
    savingsAmount: props.savingsAmount,
    savingsPercentage: props.savingsPercentage,
    coverageMatchScore: props.coverageMatchScore,
  });
}

/** Recommendation dismissed */
export function trackRecommendationDismissed(props = {}) {
  track('Recommendation Dismissed', {
    recommendationId: props.recommendationId,
    reason: props.reason ?? 'not_interested',
    viewDuration: props.viewDuration,
  });
}

// —— Category 3: Conversion ——

/** Switch initiated */
export function trackSwitchInitiated(props = {}) {
  track('Switch Initiated', {
    policyId: props.policyId,
    oldInsurer: props.oldInsurer,
    newInsurer: props.newInsurer,
    savingsAmount: props.savingsAmount,
    hasGap: props.hasGap ?? false,
    gapDays: props.gapDays ?? 0,
  });
}

/** Switch completed */
export function trackSwitchCompleted(props = {}) {
  track('Switch Completed', {
    switchId: props.switchId,
    policyId: props.policyId,
    timeTaken: props.timeTaken,
    savingsAmount: props.savingsAmount,
  });
}

/** Auto-renewal enabled/updated */
export function trackAutoRenewalEnabled(props = {}) {
  track('Auto Renewal Enabled', {
    policyId: props.policyId,
    preferCurrentInsurer: props.preferCurrentInsurer,
    allowAutoSwitch: props.allowAutoSwitch,
    maxPriceIncrease: props.maxPriceIncrease,
    minSavings: props.minSavings,
  });
}

// —— Category 4: PWA ——

export function trackPWAInstallPromptShown(props = {}) {
  track('PWA Install Prompt Shown', {
    visitCount: props.visitCount,
    context: props.context ?? 'dashboard_loaded',
  });
}

export function trackPWAInstalled(props = {}) {
  track('PWA Installed', {
    visitCount: props.visitCount,
    timeSinceFirstVisit: props.timeSinceFirstVisit,
  });
}

export function trackAppOpened(props = {}) {
  track('App Opened', {
    source: props.source ?? 'browser',
    isOnline: props.isOnline ?? true,
    cachedDataAge: props.cachedDataAge ?? 0,
  });
}

export function trackOfflineModeActivated(props = {}) {
  track('Offline Mode Activated', {
    cachedDataAge: props.cachedDataAge,
    hasUnsentActions: props.hasUnsentActions ?? false,
  });
}

// —— Category 5: Share ——

export function trackRecommendationShared(props = {}) {
  track('Recommendation Shared', {
    recommendationId: props.recommendationId,
    shareMethod: props.shareMethod ?? 'email',
    savingsAmount: props.savingsAmount,
  });
}

export function trackSharedLinkOpened(props = {}) {
  track('Shared Link Opened', {
    recommendationId: props.recommendationId,
    referrer: props.referrer,
  });
}

// —— Legacy aliases (same as Section 23 where applicable) ——
export const trackRenewalsPageViewed = trackRenewalsDashboardViewed;

export default {
  track,
  trackRenewalsDashboardViewed,
  trackRenewalsPageViewed,
  trackRenewalDetailsViewed,
  trackRenewalTabChanged,
  trackRenewalCheckStarted,
  trackRenewalCheckCompleted,
  trackRecommendationViewed,
  trackRecommendationDismissed,
  trackSwitchInitiated,
  trackSwitchCompleted,
  trackAutoRenewalEnabled,
  trackPWAInstallPromptShown,
  trackPWAInstalled,
  trackAppOpened,
  trackOfflineModeActivated,
  trackRecommendationShared,
  trackSharedLinkOpened,
};
